/**
 * Reiter *Stammdaten* an einem privaten Bereich (`kind = 'folder'`, Spec 05b,
 * „Stammdaten eines Ordners"). Ein Ordner hat keine Fachtabelle und keinen
 * eigenen `GET`-Endpunkt (Spec, „Endpunkte" — nur `POST`/`PATCH
 * /entities/private-areas`); Name und Version kommen deshalb aus `ObjectHead`,
 * das `routes/ObjectDetail.tsx` ohnehin schon lädt, statt aus einer zweiten
 * Abfrage.
 *
 * Kein Archivieren: Die Spec nennt für den privaten Bereich weder einen
 * Archivierungs- noch einen Duplizier-Weg — anders als bei
 * Grundstück/Gebäude/Einheit bleibt hier nur das eine Feld.
 */

import { useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { ApiError } from '@/lib/api'
import { ConfirmationStrip } from '@/components/ConfirmationStrip'
import { MasterDataEditSheet } from '@/components/MasterDataEditSheet'
import { useRenamePrivateArea } from '@/features/objects/api'
import { FormField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'

interface FolderMasterDataProps {
  readonly entityId: string
  readonly canEdit: boolean
  readonly label: string
  readonly version: number
}

export function FolderMasterData({ entityId, canEdit, label, version }: FolderMasterDataProps) {
  const renameMutation = useRenamePrivateArea()

  const [value, setValue] = useState(label)
  const [initial, setInitial] = useState(label)
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)
  const editTriggerRef = useRef<HTMLButtonElement>(null)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [confirmation, setConfirmation] = useState<string | null>(null)

  // Springt auf einen neuen Serverstand um, sobald `version` sich ändert
  // (nach dem Speichern oder nach einem Umhängen) — dieselbe Technik wie
  // `UnitMasterData`.
  const [syncedKey, setSyncedKey] = useState(`${entityId}:${String(version)}`)
  const currentKey = `${entityId}:${String(version)}`
  if (currentKey !== syncedKey) {
    setSyncedKey(currentKey)
    setValue(label)
    setInitial(label)
  }

  const isDirty = value !== initial

  function openEditor() {
    setValue(label)
    setInitial(label)
    setFieldError(undefined)
    setGeneralError(undefined)
    setIsEditOpen(true)
  }

  function handleEditOpenChange(open: boolean) {
    if (open) openEditor()
    else setIsEditOpen(false)
  }

  async function handleSave(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    setFieldError(undefined)
    setGeneralError(undefined)

    try {
      await renameMutation.mutateAsync({ entityId, body: { label: value }, ifMatch: String(version) })
      setIsEditOpen(false)
      setConfirmation('Änderungen gespeichert')
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  return (
    <section data-testid="folder-master-data" className="px-(--page-x) py-4">
      <div className="flex items-center justify-between">
        <dl data-testid="folder-master-data-read"><div><dt className="text-label text-muted">Name</dt><dd data-testid="folder-read-label" className="text-body text-text">{label}</dd></div></dl>
        {canEdit && (
          <button type="button" ref={editTriggerRef} data-testid="folder-edit-open" onClick={openEditor} className="border-border text-text border px-3">
            Bearbeiten
          </button>
        )}
      </div>
      <MasterDataEditSheet open={isEditOpen} onOpenChange={handleEditOpenChange} title="Privaten Bereich bearbeiten"
        triggerRef={editTriggerRef}
        formId="folder-edit-form"
        saveTestId="folder-save"
        saveDisabled={!isDirty || renameMutation.isPending}
        isSaving={renameMutation.isPending}
      >
      <form id="folder-edit-form" onSubmit={(event) => void handleSave(event)} className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
        <FormField
          label="Name"
          testId="folder-edit-label"
          apiField="label"
          errorPrefix="folder-edit"
          required
          value={value}
          onChange={setValue}
          error={fieldError}
        />

        {generalError !== undefined && (
          <p data-testid="folder-edit-error" role="alert" className="text-label text-status-overdue">
            {generalError}
          </p>
        )}

      </form>
      </MasterDataEditSheet>
      <ConfirmationStrip message={confirmation} onDismiss={() => { setConfirmation(null) }} testId="object-save-confirmation" />
    </section>
  )
}
